<template>
  <v-card class="elevation-12" :loading="loading">
    <v-toolbar
        color="primary"
        dark
        flat
    >
      <v-toolbar-title>{{ $t('editGlobalUserForm.title') }}</v-toolbar-title>
      <v-spacer></v-spacer>
    </v-toolbar>
    <v-card-text class="my-5">
      <v-form>
        <v-text-field
            v-model="form.fullname"
            :label="$t('common.labels.fullName')"
            type="text"
            maxlength="400"
            :counter="400"
            :error-messages="fullNameErrors"
        />

        <v-text-field
            v-model="form.username"
            :label="$t('common.labels.login')"
            type="text"
            maxlength="12"
            :counter="12"
            :disabled="true"
        />

        <v-text-field
            v-model="form.password"
            :label="$t('common.labels.password')"
            type="password"
            :error-messages="passwordErrors"
        />

        <v-text-field
            v-model="form.passwordRepeat"
            :label="$t('common.labels.passwordRepeat')"
            type="password"
            :error-messages="passwordRepeatErrors"
        />

        <v-combobox v-model="form.unit" :items="units" :label="$t('common.labels.department')" item-text="name" :error-messages="unitErrors" />

        <v-checkbox
            v-model="form.canSignOnBehalfOfOrganization"
            :label="$t('addGlobalUserForm.canSign')"
            color="red darken-3"
        ></v-checkbox>

        <v-radio-group v-model="form.type">
          <template v-slot:label>
            <div><strong>{{ $t('addGlobalUserForm.userType') }}</strong></div>
          </template>
          <v-radio :value="1" color="error">
            <template v-slot:label>
              <div>{{ $t('userTypes.admin') }}</div>
            </template>
          </v-radio>
          <v-radio :value="3" color="success">
            <template v-slot:label>
              <div>{{ $t('userTypes.controller') }}</div>
            </template>
          </v-radio>
          <v-radio :value="4" color="info">
            <template v-slot:label>
              <div>{{ $t('userTypes.monitoring') }}</div>
            </template>
          </v-radio>
          <v-radio :value="5" color="info">
            <template v-slot:label>
              <div>{{ $t('userTypes.auditor') }}</div>
            </template>
          </v-radio>
        </v-radio-group>

        <v-snackbar v-model="isServerError"
                    :timeout="5000"
                    :top="true"
                    color="error"
        >
          {{ $t('common.messages.serverError') }}
        </v-snackbar>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-btn text color="secondary" @click="onCancelButtonClicked">{{ $t('common.actions.cancel') }}</v-btn>
      <v-spacer/>
      <v-btn text color="primary" @click="onSaveButtonClicked">{{ $t('common.actions.save') }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import {maxLength, minLength, required, sameAs} from "vuelidate/lib/validators";
import {LAYOUT_LOADING_START, LAYOUT_LOADING_STOP} from "@/store/modules/layout/actions";
import _ from "underscore";
import Mappers from "@/mappers";
import GlobalUserService from "@/services/global.user.service";
import GlobalUnitService from "@/services/global.unit.service";

export default {
  name: "EditAdminGlobalUserComponent",
  props: {
    inn: String
  },
  data() {
    return {
      loading: false,
      form: {
        fullname: '',
        username: '',
        password: '',
        passwordRepeat: '',
        isAdmin: false,
        type: '',
        unit: null,
        canSignOnBehalfOfOrganization: false
      },
      units: [],
      isServerError: false
    }
  },
  computed: {
    fullNameErrors() {
      const errors = []

      if (this.$v.form.fullname.$dirty) {
        if (!this.$v.form.fullname.required) {
          errors.push(this.$t('common.errors.fullNameRequired'))
        } else {
          !this.$v.form.fullname.maxLength && errors.push(this.$t('common.errors.fullNameTooLong'))
        }
      }

      return errors
    },
    passwordErrors() {
      const errors = []

      if (this.$v.form.password.$dirty) {
        if (!this.$v.form.password.required) {
          errors.push(this.$t('common.errors.passwordRequired'))
        } else {
          if (!this.$v.form.password.minLength)
            errors.push(this.$t('common.errors.passwordTooShort'))
        }
      }

      return errors
    },
    passwordRepeatErrors() {
      const errors = []

      if (this.$v.form.passwordRepeat.$dirty) {
        if (!this.$v.form.passwordRepeat.required) {
          errors.push(this.$t('common.errors.passwordRepeatRequired'))
        } else {
          if (!this.$v.form.passwordRepeat.sameAs) {
            errors.push(this.$t('common.errors.passwordsDontMatch'))
          }
        }
      }

      return errors
    },
    unitErrors() {
      const errors = []

      if (this.$v.form.unit.$dirty)
        if (!this.$v.form.unit.required)
          errors.push(this.$t('common.errors.departmentRequired'))

      return errors
    }
  },
  validations: {
    form: {
      fullname: {
        required,
        maxLength: maxLength(400)
      },
      password: {
        required,
        minLength: minLength(8),
      },
      passwordRepeat: {
        required,
        sameAs: sameAs(function () {
          return this.form.password
        })
      },
      unit: {
        required
      }
    }
  },
  methods: {
    set(user) {
      this.clear()

      this.form.username = user.username
      this.form.fullname = user.name
      this.form.type = user.type
      this.form.canSignOnBehalfOfOrganization = user.canSignOnBehalfOfOrganization

      this.loadUnits(user.globalorganization.inn, user.globalunit.id)
    },
    clear() {
      this.$v.$reset()
      this.form.fullname = ''
      this.form.username = ''
      this.form.password = ''
      this.form.passwordRepeat = ''
      this.form.type = ''
    },
    loadUnits(inn, unitId) {
      GlobalUnitService.getUserUnits({inn: inn})
          .then(response => {
            this.units = _.map(response.data, o => {
              return Mappers.mapGlobalUnit(o)
            })

            this.form.unit = _.find(this.units, u => {
              return u.id === unitId
            }) || null
          })
    },

    onCancelButtonClicked() {
      this.clear()
      this.$emit('cancel')
    },
    onSaveButtonClicked() {
      this.$v.form.$touch()
      if (!this.$v.form.$invalid) {
        this.loading = true;
        this.$store.dispatch(LAYOUT_LOADING_START)

        this.isServerError = false

        GlobalUserService.editUser({
          userFullName: this.form.fullname,
          username: this.form.username,
          password: this.form.password,
          type: this.form.type,
          unitId: this.form.unit.id,
          canSignOnBehalfOfOrganization: this.form.canSignOnBehalfOfOrganization
        })
            .then(() => {
              this.clear()
              this.$emit('updated')
            }, () => {
              this.isServerError = true
            })
            .finally(() => {
              this.loading = false
              this.$store.dispatch(LAYOUT_LOADING_STOP)
            })
      }
    }
  }
}
</script>

<style scoped>

</style>